import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { TabsContent } from '../../components/ui/tabs';
import { getWarrantyError, hasWarrantyErrors } from '../../utils/validateWarranty';
import { NumericInput } from '../../components/NumericInput';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { Plus, Trash2, Edit2, Upload, ClipboardPaste, Loader2, X, Image as ImageIcon, Info } from 'lucide-react';
import ColorPickerPopover from '../../components/ColorPickerPopover';

export const CWTypesSection = ({
  // Data
  curtainwallTypes,
  curtainwallElevations,
  selectedCurtainwallTypeIds,
  newCWType,
  editingCWType,
  showCWTypeDialog,
  cwTypePictureUrl,
  pendingCWTypePicture,
  cwTypePictureUploading,
  operationLoading,
  // Permissions
  canEditCSIItems,
  canManageProject,
  // Handlers
  setNewCWType,
  setShowCWTypeDialog,
  setEditingCWType,
  setCWTypePictureUrl,
  setPendingCWTypePicture,
  setCurtainwallTypes,
  selectAllItems,
  confirmBulkDelete,
  toggleItemSelection,
  handleCreateCWType,
  handleEditCWType,
  handleDeleteCWType,
  handleCWDrawingUpload,
  handleCWTypePasteFromClipboard,
  handleDeleteDrawing,
  handleOpenCWDrawingView,
  // API
  API,
  axios,
  toast,
}) => {
  return (
    <TabsContent value="cw-types">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Curtainwall Types</h2>
        <div className="flex gap-2">
          {canEditCSIItems && curtainwallTypes.length > 0 && (
            <>
              <Button 
                variant="outline"
                onClick={() => selectAllItems('curtainwallType')}
              >
                {selectedCurtainwallTypeIds.length === curtainwallTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={() => confirmBulkDelete('curtainwallType')}
                disabled={selectedCurtainwallTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedCurtainwallTypeIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Dialog open={showCWTypeDialog} onOpenChange={(open) => {
              if (open && !editingCWType) {
                // Clear previous clipboard upload preview and reset form when opening Add dialog
                setCWTypePictureUrl(null);
                if (pendingCWTypePicture?.previewUrl) {
                  URL.revokeObjectURL(pendingCWTypePicture.previewUrl);
                }
                setPendingCWTypePicture(null);
                setNewCWType({
                  name: '',
                  manufacturer: '',
                  model: '',
                  material: '',
                  finishType: '',
                  interiorFinishColor: '',
                  exteriorFinishColor: '',
                  sectionWidth_in: null,
                  sectionHeight_in: null,
                  roWidth_in: null,
                  roHeight_in: null,
                  productWarrantyYears: null,
                  hardwareWarrantyYears: null,
                  finishWarrantyYears: null,
                  installationWarrantyYears: null,
                  markerColor: '#39FF14'
                });
              }
              setShowCWTypeDialog(open);
              if (!open) {
                setEditingCWType(null);
                setCWTypePictureUrl(null);
                if (pendingCWTypePicture?.previewUrl) {
                  URL.revokeObjectURL(pendingCWTypePicture.previewUrl);
                }
                setPendingCWTypePicture(null);
                setNewCWType({
                  name: '',
                  manufacturer: '',
                  model: '',
                  material: '',
                  finishType: '',
                  interiorFinishColor: '',
                  exteriorFinishColor: '',
                  sectionWidth_in: null,
                  sectionHeight_in: null,
                  roWidth_in: null,
                  roHeight_in: null,
                  productWarrantyYears: null,
                  hardwareWarrantyYears: null,
                  finishWarrantyYears: null,
                  installationWarrantyYears: null,
                  markerColor: '#39FF14'
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add CW Type
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle>{editingCWType ? 'Edit Curtainwall Type' : 'Create Curtainwall Type'}</DialogTitle>
                    <DialogDescription>Define a curtainwall system type</DialogDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Marker Color</Label>
                    <input
                      type="color"
                      value={newCWType.markerColor || '#39FF14'}
                      onChange={(e) => setNewCWType({...newCWType, markerColor: e.target.value})}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                      title="Choose marker color for elevations"
                    />
                  </div>
                </div>
              </DialogHeader>
              <form onSubmit={handleCreateCWType} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type (e.g., CW1, CW2) *</Label>
                    <Input value={newCWType.name} onChange={(e) => setNewCWType({...newCWType, name: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Manufacturer *</Label>
                    <Input value={newCWType.manufacturer} onChange={(e) => setNewCWType({...newCWType, manufacturer: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Model *</Label>
                    <Input value={newCWType.model} onChange={(e) => setNewCWType({...newCWType, model: e.target.value})} required />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Material *</Label>
                  <div className="flex gap-6">
                    {['Aluminum', 'Steel', 'Wood'].map((mat) => (
                      <div key={mat} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id={`material-${mat}`}
                          name="material"
                          checked={newCWType.material === mat}
                          onChange={() => setNewCWType({...newCWType, material: mat})}
                          className="w-4 h-4 text-orange-600 cursor-pointer"
                        />
                        <label htmlFor={`material-${mat}`} className="text-sm font-medium cursor-pointer">{mat}</label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Finish Type *</Label>
                    <Input value={newCWType.finishType} onChange={(e) => setNewCWType({...newCWType, finishType: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Interior Finish Color *</Label>
                    <Input value={newCWType.interiorFinishColor} onChange={(e) => setNewCWType({...newCWType, interiorFinishColor: e.target.value})} placeholder="e.g., White, RAL 9016" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Exterior Finish Color *</Label>
                    <Input value={newCWType.exteriorFinishColor} onChange={(e) => setNewCWType({...newCWType, exteriorFinishColor: e.target.value})} placeholder="e.g., Bronze, RAL 8001" required />
                  </div>
                </div>
                
                {/* Section Dimensions */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm border-b pb-2">Section Dimensions (in)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Width (in)</Label>
                      <NumericInput positive allowFraction value={newCWType.sectionWidth_in || ''} onChange={(v) => setNewCWType({...newCWType, sectionWidth_in: v})} placeholder="e.g., 6.0" />
                    </div>
                    <div className="space-y-2">
                      <Label>Height (in)</Label>
                      <NumericInput positive allowFraction value={newCWType.sectionHeight_in || ''} onChange={(v) => setNewCWType({...newCWType, sectionHeight_in: v})} placeholder="e.g., 120.0" />
                    </div>
                  </div>
                </div>
                
                {/* Rough Openings */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm border-b pb-2">Rough Openings (in)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>RO Width (in)</Label>
                      <NumericInput positive allowFraction value={newCWType.roWidth_in || ''} onChange={(v) => setNewCWType({...newCWType, roWidth_in: v})} placeholder="e.g., 6.5" />
                    </div>
                    <div className="space-y-2">
                      <Label>RO Height (in)</Label>
                      <NumericInput positive allowFraction value={newCWType.roHeight_in || ''} onChange={(v) => setNewCWType({...newCWType, roHeight_in: v})} placeholder="e.g., 121.0" />
                    </div>
                  </div>
                </div>
                
                {/* Picture Upload */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm border-b pb-2">CW Type Picture</h4>
                  {/* Show existing drawing for edit mode */}
                  {editingCWType && (cwTypePictureUrl || editingCWType?.drawingCloudinary?.urls?.medium || editingCWType?.drawingUrl) && (
                    <div className="mb-4">
                      <img 
                        src={cwTypePictureUrl || editingCWType?.drawingCloudinary?.urls?.medium || `${API}${editingCWType?.drawingUrl}`} 
                        alt="CW type drawing" 
                        className="max-w-full h-auto max-h-64 border rounded"
                      />
                    </div>
                  )}
                  {/* Show pending drawing preview for add mode */}
                  {!editingCWType && pendingCWTypePicture?.previewUrl && (
                    <div className="mb-4">
                      <div className="relative inline-block">
                        <img 
                          src={pendingCWTypePicture.previewUrl} 
                          alt="Pending CW type drawing" 
                          className="max-w-full h-auto max-h-64 border rounded"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => {
                            URL.revokeObjectURL(pendingCWTypePicture.previewUrl);
                            setPendingCWTypePicture(null);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-green-600 mt-2">✓ Picture ready - will be uploaded when CW type is created</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Upload Picture (PNG/JPEG/PDF)</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleCWTypePasteFromClipboard(editingCWType?.id || null)}
                        disabled={cwTypePictureUploading === editingCWType?.id}
                      >
                        {cwTypePictureUploading === editingCWType?.id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <ClipboardPaste className="h-4 w-4 mr-2" />
                        )}
                        Paste
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('cw-type-drawing-modal').click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {(cwTypePictureUrl || editingCWType?.drawingCloudinary?.urls?.medium || editingCWType?.drawingUrl || pendingCWTypePicture) ? 'Change Picture' : 'Upload Picture'}
                      </Button>
                      <input
                        id="cw-type-drawing-modal"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,application/pdf"
                        onChange={(e) => {
                          if (editingCWType) {
                            handleCWDrawingUpload(e, editingCWType.id);
                          } else {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (pendingCWTypePicture?.previewUrl) {
                                URL.revokeObjectURL(pendingCWTypePicture.previewUrl);
                              }
                              const previewUrl = URL.createObjectURL(file);
                              setPendingCWTypePicture({ file, previewUrl });
                            }
                          }
                          e.target.value = '';
                        }}
                        style={{ display: 'none' }}
                      />
                    </div>
                    {!editingCWType && !pendingCWTypePicture && (
                      <p className="text-sm text-slate-500">Picture will be uploaded after creating the type</p>
                    )}
                  </div>
                </div>
                
                {/* Warranty Periods Section */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Warranty Periods (Years)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Product Warranty (Years)</Label>
                      <NumericInput value={newCWType.productWarrantyYears || ''} onChange={(v) => setNewCWType({...newCWType, productWarrantyYears: v ? parseInt(v) : null})} placeholder="e.g., 10" className={getWarrantyError(newCWType.productWarrantyYears) ? 'border-red-500' : ''} data-testid="cw-product-warranty-input" />
                      {getWarrantyError(newCWType.productWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newCWType.productWarrantyYears)}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Hardware Warranty (Years)</Label>
                      <NumericInput value={newCWType.hardwareWarrantyYears || ''} onChange={(v) => setNewCWType({...newCWType, hardwareWarrantyYears: v ? parseInt(v) : null})} placeholder="e.g., 5" className={getWarrantyError(newCWType.hardwareWarrantyYears) ? 'border-red-500' : ''} data-testid="cw-hardware-warranty-input" />
                      {getWarrantyError(newCWType.hardwareWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newCWType.hardwareWarrantyYears)}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Finish Warranty (Years)</Label>
                      <NumericInput value={newCWType.finishWarrantyYears || ''} onChange={(v) => setNewCWType({...newCWType, finishWarrantyYears: v ? parseInt(v) : null})} placeholder="e.g., 15" className={getWarrantyError(newCWType.finishWarrantyYears) ? 'border-red-500' : ''} data-testid="cw-finish-warranty-input" />
                      {getWarrantyError(newCWType.finishWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newCWType.finishWarrantyYears)}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Installation Warranty (Years)</Label>
                      <NumericInput value={newCWType.installationWarrantyYears || ''} onChange={(v) => setNewCWType({...newCWType, installationWarrantyYears: v ? parseInt(v) : null})} placeholder="e.g., 2" className={getWarrantyError(newCWType.installationWarrantyYears) ? 'border-red-500' : ''} data-testid="cw-installation-warranty-input" />
                      {getWarrantyError(newCWType.installationWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newCWType.installationWarrantyYears)}</p>}
                    </div>
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={operationLoading || hasWarrantyErrors([newCWType.productWarrantyYears, newCWType.hardwareWarrantyYears, newCWType.finishWarrantyYears, newCWType.installationWarrantyYears])}>
                  {operationLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingCWType ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingCWType ? 'Update CW Type' : 'Create CW Type'
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {curtainwallTypes.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-600">No curtainwall types created yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {curtainwallTypes.map((cw, index) => (
            <Card key={cw.id} className={selectedCurtainwallTypeIds.includes(cw.id) ? 'ring-2 ring-blue-500' : ''}>
              <div className="flex">
                {/* Checkbox column */}
                {canEditCSIItems && (
                  <div className="w-12 flex-shrink-0 flex items-start justify-center pt-4 border-r">
                    <Checkbox
                      checked={selectedCurtainwallTypeIds.includes(cw.id)}
                      onCheckedChange={(checked) => toggleItemSelection(cw.id, 'curtainwallType', { target: { checked } })}
                      className="h-5 w-5"
                    />
                  </div>
                )}
                
                {/* Picture column - Left */}
                <div className="flex-shrink-0 p-4 border-r" style={{ width: '250px', height: '300px' }}>
                  {(cw.drawingCloudinary?.urls?.medium || cw.drawingUrl) ? (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        <div className="flex gap-1">
                          {canEditCSIItems && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCWTypePasteFromClipboard(cw.id)}
                                title="Paste image from clipboard"
                                disabled={cwTypePictureUploading === cw.id}
                              >
                                {cwTypePictureUploading === cw.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ClipboardPaste className="h-3 w-3" />
                                )}
                              </Button>
                              <label className="cursor-pointer">
                                <Button variant="outline" size="sm" asChild>
                                  <span><Upload className="h-3 w-3" /></span>
                                </Button>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept=".pdf,.jpg,.jpeg,.png,.dwg"
                                  onChange={(e) => handleCWDrawingUpload(e, cw.id)}
                                />
                              </label>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteDrawing(cw.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        className="flex-1 cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center overflow-hidden"
                        onClick={() => handleOpenCWDrawingView(cw)}
                      >
                        <img
                          src={cw.drawingCloudinary?.urls?.large || `${API}${cw.drawingUrl}?t=${cw.updatedAt || cw.createdAt || 'default'}`}
                          alt="Curtainwall Type Picture"
                          className="max-w-full max-h-full object-contain border rounded"
                        />
                      </div>
                      {cw.drawingMarkers && cw.drawingMarkers.length > 0 && (
                        <div className="text-xs text-slate-500 mt-2 text-center">
                          {cw.drawingMarkers.length} schedule(s) marked
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        {canEditCSIItems && (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCWTypePasteFromClipboard(cw.id)}
                              title="Paste image from clipboard"
                              disabled={cwTypePictureUploading === cw.id}
                            >
                              {cwTypePictureUploading === cw.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <ClipboardPaste className="h-3 w-3" />
                              )}
                            </Button>
                            <label className="cursor-pointer">
                              <Button variant="outline" size="sm" asChild>
                                <span><Upload className="h-3 w-3" /></span>
                              </Button>
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png,.dwg"
                                onChange={(e) => handleCWDrawingUpload(e, cw.id)}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex items-center justify-center">
                        {canManageProject ? (
                          <label className="cursor-pointer w-full">
                            <div className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-slate-50 transition-colors">
                              <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                              <p className="text-sm text-slate-600">Upload Picture</p>
                              <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG</p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png,.dwg"
                              onChange={(e) => handleCWDrawingUpload(e, cw.id)}
                            />
                          </label>
                        ) : (
                          <div className="text-center text-slate-400">
                            <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                            <p className="text-sm">No Picture</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Content column - Right */}
                <div className="flex-1 p-4 flex flex-col justify-between" style={{ height: '300px' }}>
                  {/* Action buttons */}
                  {canManageProject && (
                    <div className="flex gap-2 items-center justify-end mb-2">
                      <ColorPickerPopover
                        id={`cw-${cw.id}`}
                        currentColor={cw.markerColor}
                        pendingColor={cw.pendingMarkerColor}
                        isOpen={cw.colorPickerOpen}
                        isSaving={cw.savingColor}
                        onOpenChange={(open) => {
                          setCurtainwallTypes(prev => prev.map(t => 
                            t.id === cw.id ? { ...t, colorPickerOpen: open, pendingMarkerColor: open ? t.pendingMarkerColor : undefined } : t
                          ));
                        }}
                        onColorChange={(newColor) => {
                          setCurtainwallTypes(prev => prev.map(t => 
                            t.id === cw.id ? { ...t, pendingMarkerColor: newColor } : t
                          ));
                        }}
                        onSave={async () => {
                          setCurtainwallTypes(prev => prev.map(t => 
                            t.id === cw.id ? { ...t, savingColor: true } : t
                          ));
                          try {
                            await axios.put(`${API}/curtainwall-types/${cw.id}`, {
                              ...cw,
                              markerColor: cw.pendingMarkerColor
                            });
                            setCurtainwallTypes(prev => prev.map(t => 
                              t.id === cw.id ? { ...t, markerColor: cw.pendingMarkerColor, pendingMarkerColor: undefined, savingColor: false, colorPickerOpen: false } : t
                            ));
                            toast.success('Marker color saved');
                          } catch (error) {
                            setCurtainwallTypes(prev => prev.map(t => 
                              t.id === cw.id ? { ...t, savingColor: false } : t
                            ));
                            toast.error('Failed to update marker color');
                          }
                        }}
                      />
                      <Button variant="outline" size="sm" onClick={() => handleEditCWType(cw)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteCWType(cw.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* 4 Rows of content - fixed width columns */}
                  <div className="flex-1 flex flex-col justify-evenly text-sm">
                    {/* Row 1: Name, Manufacturer, Model */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Type</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.name}</div>
                      <div className="font-semibold w-[95px]">Manufacturer</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.manufacturer}</div>
                      <div className="font-semibold w-[95px]">Model</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.model}</div>
                    </div>
                    
                    {/* Row 2: Material, Finish Type, Interior Color, Exterior Color */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Material</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.material || 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Finish Type</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.finishType}</div>
                      <div className="font-semibold w-[95px]">Interior Color</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.interiorFinishColor}</div>
                      <div className="font-semibold w-[95px]">Exterior Color</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.exteriorFinishColor}</div>
                    </div>
                    
                    {/* Row 3: Section Dim Width/Height, Rough Open Width/Height */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Section Dim Width</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.sectionWidth_in ? `${cw.sectionWidth_in}"` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Section Dim Height</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.sectionHeight_in ? `${cw.sectionHeight_in}"` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Rough Open Width</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.roWidth_in ? `${cw.roWidth_in}"` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Rough Open Height</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.roHeight_in ? `${cw.roHeight_in}"` : 'N/A'}</div>
                    </div>
                    
                    {/* Row 4: Warranty Periods */}
                    <div className="flex">
                      <div className="font-semibold w-[95px]">Product Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.productWarrantyYears ? `${cw.productWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Hardware Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.hardwareWarrantyYears ? `${cw.hardwareWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Install Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{cw.installationWarrantyYears ? `${cw.installationWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[105px] flex items-center gap-1">
                        Quantity
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Schedule Usage</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[60px] font-medium text-orange-600">{
                        curtainwallElevations.reduce((count, elev) => {
                          return count + (elev.markers?.filter(m => m.windowListId === cw.id).length || 0);
                        }, 0)
                      }</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </TabsContent>
  );
};

export default CWTypesSection;
